import {isFunction} from '#lit/utils/index.js';
import {hasProperty} from '#lit/utils/objects.js';
import type {ChannelController} from '#lit/controllers/state_controller/index.js';
import type {
  ActionsMap,
  ConsumptionStrategy,
  ProvideMap,
} from '#lit/types/state_controller/index.js';

// This types are used as extends boundaries,
// so we must rely on any as unknown is too specific to be extended.
/* eslint-disable @typescript-eslint/no-explicit-any */
type GenericConstructor = new (...args: any[]) => object;
type GenericChannelController = ChannelController<any, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

type EnqueuedListenersRecord = Record<
  string,
  Array<{action: string; key: string}>
>;

type EnqueuedProvidersRecord<TProvide extends ProvideMap> = Record<
  string,
  Array<{
    key: keyof TProvide;
    property: TProvide[keyof TProvide];
  }>
>;

type EnqueuedConsumersRecord<TController extends GenericChannelController> =
  Record<
    string,
    Array<{
      decoratedProperty: string;
      provideKey: ExtractedProviderKeyMap<TController>;
      strategy: ConsumptionStrategy<
        ExtractedProvideMap<TController>,
        ExtractedProviderKeyMap<TController>
      >;
    }>
  >;

type ExtractedActionMap<T> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ChannelController<infer Actions, any>
    ? Actions extends ActionsMap
      ? Actions
      : never
    : never;

type ExtractedActionKeyMap<T> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ChannelController<infer Actions, any>
    ? Actions extends ActionsMap
      ? keyof Actions extends string
        ? keyof Actions
        : string
      : never
    : never;

type ExtractedProvideMap<T> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ChannelController<any, infer Provide>
    ? Provide extends ProvideMap
      ? Provide
      : never
    : never;

export type ExtractedProviderKeyMap<T> =
  T extends ChannelController<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    infer Provide
  >
    ? Provide extends ProvideMap
      ? keyof Provide extends string
        ? keyof Provide
        : string
      : never
    : never;

type ExtendedConstructor<T> = T &
  Record<typeof ENQUEUED_LISTENERS_SYMBOL, EnqueuedListenersRecord> &
  Record<
    typeof ENQUEUED_PROVIDERS_SYMBOL,
    EnqueuedProvidersRecord<ProvideMap>
  > &
  Record<
    typeof ENQUEUED_CONSUMERS_SYMBOL,
    EnqueuedConsumersRecord<GenericChannelController>
  >;

const DEFAULT_CONTROLLER_PROPERTY = 'controller';
const ENQUEUED_LISTENERS_SYMBOL = Symbol('controller-on');
const ENQUEUED_PROVIDERS_SYMBOL = Symbol('controller-providers');
const ENQUEUED_CONSUMERS_SYMBOL = Symbol('controller-consumers');

function getControllerPropertyName(params?: {controller?: string}): string {
  if (!params || !params.controller) {
    return 'controller';
  }
  return params.controller;
}

function extendConstructor<T extends GenericConstructor>(
  constructor: T,
  property: string
): ExtendedConstructor<T> {
  if (!hasProperty(constructor, ENQUEUED_LISTENERS_SYMBOL)) {
    Object.assign(constructor, {[ENQUEUED_LISTENERS_SYMBOL]: {}});
  }
  if (!hasProperty(constructor, ENQUEUED_PROVIDERS_SYMBOL)) {
    Object.assign(constructor, {[ENQUEUED_PROVIDERS_SYMBOL]: {}});
  }
  if (!hasProperty(constructor, ENQUEUED_CONSUMERS_SYMBOL)) {
    Object.assign(constructor, {[ENQUEUED_CONSUMERS_SYMBOL]: {}});
  }

  const _item = constructor as ExtendedConstructor<T>;

  if (!hasProperty(_item[ENQUEUED_LISTENERS_SYMBOL], property)) {
    Object.assign(_item[ENQUEUED_LISTENERS_SYMBOL], {
      [property]: [],
    });
  }
  if (!hasProperty(_item[ENQUEUED_PROVIDERS_SYMBOL], property)) {
    Object.assign(_item[ENQUEUED_PROVIDERS_SYMBOL], {
      [property]: [],
    });
  }
  if (!hasProperty(_item[ENQUEUED_CONSUMERS_SYMBOL], property)) {
    Object.assign(_item[ENQUEUED_CONSUMERS_SYMBOL], {
      [property]: [],
    });
  }

  return _item;
}

export function stateController<
  TActions extends ActionsMap,
  TProvide extends ActionsMap,
>(
  controllerInstance: ChannelController<TActions, TProvide>,
  controllerProperty = DEFAULT_CONTROLLER_PROPERTY
) {
  return function stateControllerDecorator<T>(OriginalClass: T) {
    const _class = extendConstructor(
      OriginalClass as GenericConstructor,
      controllerProperty
    );

    return class extends (OriginalClass as GenericConstructor) {
      // Typescript requires a mixin to use (...args: any[]) signature
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      constructor(...args: any[]) {
        super(...args);
        Object.assign(this, {[controllerProperty]: controllerInstance});
        const listeners = _class[ENQUEUED_LISTENERS_SYMBOL];
        const providers = _class[ENQUEUED_PROVIDERS_SYMBOL];
        const consumers = _class[ENQUEUED_CONSUMERS_SYMBOL];

        listeners[controllerProperty].forEach(({action, key}) => {
          if (hasProperty(this, key, isFunction)) {
            controllerInstance.onAction(this, action, this[key].bind(this));
          }
        });

        consumers[controllerProperty].forEach(consumer => {
          const fn = controllerInstance.setConsumer(
            this,
            consumer.provideKey,
            consumer.strategy
          );
          if (fn) {
            Object.assign(this, {[consumer.decoratedProperty]: fn});
          }
        });

        const providedKeys = providers[controllerProperty];

        providedKeys.forEach(provideItem => {
          controllerInstance.setProvider(
            this as unknown as Record<string | symbol, unknown>,
            provideItem
          );
        });
      }
    } as T;
  };
}

export function on<TController extends GenericChannelController>(
  action:
    | ExtractedActionKeyMap<TController>
    | Array<ExtractedActionKeyMap<TController>>,
  options?: {controller?: string}
) {
  return function onDecorator(
    target: object,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const controllerProperty = getControllerPropertyName(options);
    const actions = Array.isArray(action) ? action : [action];

    if (
      !hasProperty<object, 'constructor', GenericConstructor>(
        target,
        'constructor'
      )
    ) {
      throw new Error(`The target "${target}" has no constructor property.`);
    }

    const constructor = extendConstructor(
      target.constructor,
      controllerProperty
    );

    const enqueuedListeners = constructor[ENQUEUED_LISTENERS_SYMBOL];
    if (!hasProperty(enqueuedListeners, controllerProperty)) {
      throw new Error(
        [
          `The method "${propertyKey}"`,
          `on class "${constructor.name}" is being decorated,`,
          `but the controller property "${controllerProperty}" doesn't exist.`,
        ].join(' ')
      );
    }

    actions.forEach(action => {
      enqueuedListeners[controllerProperty].push({
        action,
        key: propertyKey,
      });
    });

    return descriptor;
  };
}

type ActionDecoratorOptions<
  TController extends GenericChannelController,
  Target = unknown,
  TActionName extends
    ExtractedActionKeyMap<TController> = ExtractedActionKeyMap<TController>,
> = {
  controller?: string;
  swallowError?: boolean;
  onSuccess?:
    | ExtractedActionMap<TController>[TActionName]
    | ((
        payload: unknown,
        item: Target,
        abort: VoidFunction
      ) => ExtractedActionMap<TController>[TActionName]);
  onError?:
    | ExtractedActionMap<TController>[TActionName]
    | ((
        error: unknown,
        item: Target,
        abort: VoidFunction
      ) => ExtractedActionMap<TController>[TActionName]);
};

export function action<
  TController extends GenericChannelController,
  Target = unknown,
  TAction extends
    ExtractedActionKeyMap<TController> = ExtractedActionKeyMap<TController>,
>(
  action: TAction,
  options?: ActionDecoratorOptions<TController, Target, TAction>
) {
  return function actionDecorator(
    target: Target,
    _propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const controllerProperty = getControllerPropertyName(options);
    const originalFunction = descriptor.value;

    descriptor.value = function decoratedFunction(
      this: Target,
      ...args: unknown[]
    ) {
      const abortController = new AbortController();
      const abortCallback = (reason?: string) => abortController.abort(reason);
      let result;
      let controller: TController;

      // NO CONTROLLER ADDED TO THE CLASS
      if (!hasProperty<Target, string, TController>(this, controllerProperty)) {
        const targetName = hasProperty<Target, 'constructor', {name: string}>(
          target,
          'constructor'
        )
          ? target.constructor.name
          : target;

        console.error(
          [
            `Unable to emit ${action.toString()} action`,
            `because there is no property ${controllerProperty} on`,
            targetName,
          ].join(' ')
        );
        return originalFunction.apply(this, args);
      } else {
        controller = this[controllerProperty];
      }

      try {
        result = originalFunction.apply(this, args);

        // ASYNC CASE - Result is a Thenable object
        if (hasProperty(result, 'then', isFunction)) {
          result = result.then((value: unknown) => {
            const actionValue = hasProperty(options, 'onSuccess')
              ? isFunction(options.onSuccess)
                ? options.onSuccess(value, this, abortCallback)
                : options.onSuccess
              : value;

            if (!abortController.signal.aborted) {
              controller.emitAction(action, actionValue);
            }

            return value;
          });

          result = result.catch((error: unknown) => {
            const actionError = hasProperty(options, 'onError')
              ? isFunction(options.onError)
                ? options.onError(error, this, abortCallback)
                : options.onError
              : error;

            if (!abortController.signal.aborted) {
              controller.emitAction(action, actionError);
            }

            return options?.swallowError ? undefined : error;
          });

          return result;
        }

        // SYNC VALUE
        const actionValue = hasProperty(options, 'onSuccess')
          ? isFunction(options.onSuccess)
            ? options.onSuccess(result, this, abortCallback)
            : options.onSuccess
          : result;

        if (!abortController.signal.aborted) {
          controller.emitAction(action, actionValue);
        }

        return result;
      } catch (error) {
        // SYNC VALUE
        const actionError = hasProperty(options, 'onError')
          ? isFunction(options.onError)
            ? options.onError(error, this, abortCallback)
            : options.onError
          : error;

        if (!abortController.signal.aborted) {
          controller.emitAction(action, actionError);
        }

        if (!options?.swallowError) {
          throw error;
        }
      }

      return result;
    };

    return descriptor;
  };
}

export function voidAction<TController extends GenericChannelController>(
  action: ExtractedActionKeyMap<TController>,
  options?: {controller?: string}
) {
  return function voidActionDecorator(
    _target: unknown,
    _propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const controllerProperty = getControllerPropertyName(options);
    const fn = descriptor.value;

    descriptor.value = function (...args: unknown[]) {
      const result = fn.apply(this, args);

      if (
        !hasProperty<unknown, typeof controllerProperty, TController>(
          this,
          controllerProperty
        )
      ) {
        console.error(
          [
            `Unable to emit ${action.toString()} action`,
            `because there is no property ${controllerProperty} on`,
            this.constructor.name,
          ].join(' ')
        );
        return result;
      }

      this[controllerProperty].emitAction(action);

      return result;
    };

    return descriptor;
  };
}

export function provide<TController extends GenericChannelController>(
  provideKey: ExtractedProviderKeyMap<TController>,
  options?: {controller?: string}
) {
  return function provideDecorator(target: object, propertyKey: string) {
    const controllerProperty = getControllerPropertyName(options);

    if (
      !hasProperty<object, 'constructor', GenericConstructor>(
        target,
        'constructor'
      )
    ) {
      throw new Error(`The target "${target}" has no constructor property.`);
    }

    const constructor = extendConstructor(
      target.constructor,
      controllerProperty
    );

    const enqueuedProviders = constructor[ENQUEUED_PROVIDERS_SYMBOL];
    if (!hasProperty(enqueuedProviders, controllerProperty)) {
      throw new Error(
        [
          `The method "${propertyKey}"`,
          `on class "${constructor.name}" is being decorated,`,
          `but the controller property "${controllerProperty}" doesn't exist.`,
        ].join(' ')
      );
    }

    enqueuedProviders[controllerProperty].push({
      key: provideKey,
      property: propertyKey,
    });
  };
}

export function consume<TController extends GenericChannelController>(
  provideKey: ExtractedProviderKeyMap<TController>,
  options: {
    controller?: string;
    strategy: ConsumptionStrategy<
      ExtractedProvideMap<TController>,
      ExtractedProviderKeyMap<TController>
    >;
  }
) {
  return function consumeDecorator(
    target: object,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const controllerProperty = getControllerPropertyName(options);

    if (
      !hasProperty<object, 'constructor', GenericConstructor>(
        target,
        'constructor'
      )
    ) {
      throw new Error(`The target "${target}" has no constructor property.`);
    }

    const constructor = extendConstructor(
      target.constructor,
      controllerProperty
    );

    const enqueuedConsumers = constructor[ENQUEUED_CONSUMERS_SYMBOL];
    if (!hasProperty(enqueuedConsumers, controllerProperty)) {
      throw new Error(
        [
          `The method "${propertyKey}"`,
          `on class "${constructor.name}" is being decorated,`,
          `but the controller property "${controllerProperty}" doesn't exist.`,
        ].join(' ')
      );
    }

    enqueuedConsumers[controllerProperty].push({
      decoratedProperty: propertyKey,
      provideKey,
      strategy: options.strategy,
    });

    return descriptor;
  };
}
